var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from) {
    for (var i = 0, il = from.length, j = to.length; i < il; i++, j++)
        to[j] = from[i];
    return to;
};
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import moment from 'moment';
import { flatten, indexBy, map, pipe, prop, range, reverse } from 'ramda';
import throttleBy from 'lodash/fp/throttle';
/**
 * Takes a record and returns its id property.
 *
 * Usage:
 * ```
 * getId({ id: 'foo' }); // returns 'foo';
 * ```
 */
export var getId = prop('id');
/**
 * Takes a record and returns its data property.
 *
 * Usage:
 * ```
 * getData({ data: 'foo' }); // returns 'foo';
 * ```
 */
export var getData = prop('data');
/**
 * Takes an array of entities having the 'id' property defined and
 * returns a new array of ids only derived from the first one.
 *
 * Usage:
 * ```
 * const entities =  [{ id: 'foo' }, { id: 'bar' }];
 * toEntitiesIds(entities); // returns ['foo', 'bar'];
 * ```
 */
export var toEntityIdsList = map(getId);
/**
 * Converts an array of entities, to an id-indexed map of entities.
 * It assumes the provided entities have the 'id' property defined;
 *
 * Usage:
 * ```
 * const entities =  [{ id: 'foo' }, { id: 'bar' }];
 * toEntitiesIds(entities); // returns ['foo', 'bar'];
 * ```
 */
export var toEntityMapById = indexBy(getId);
/**
 * Takes the response of a widget API and returns an array of search criteria
 */
export var reduceToSearchCriteria = pipe(prop('widgetGroups'), map(prop('group')), map(prop('newspapers')), flatten, map(function (_a) {
    var id = _a.id, title = _a.title, name = _a.name;
    return ({ id: id, name: name, title: title });
}));
/**
 * Takes the response of a newspaper API and returns a single newspaper.
 *
 * @param withPublicationId: When the newspaper is the resulting response from the API call to retrieve newspapers by month,
 *                           the 'id' property doesn't contain the withPublicationId. Hence, we don't want to pass it on.
 */
export var reduceToSingleNewspaper = function (withPublicationId) {
    if (withPublicationId === void 0) { withPublicationId = true; }
    return pipe(function (_a) {
        var id = _a.id, title = _a.title, name = _a.name, imgMobUrl = _a.imgMobUrl, imgSmallUrl = _a.imgSmallUrl, imgThumbUrl = _a.imgThumbUrl, imgUrl = _a.imgUrl, datePublished = _a.datePublished;
        return (__assign(__assign(__assign({ id: id,
            title: title,
            imgMobUrl: imgMobUrl,
            imgSmallUrl: imgSmallUrl,
            imgThumbUrl: imgThumbUrl,
            imgUrl: imgUrl }, withPublicationId ? { publicationId: id.toString() } : {}), name ? { name: name } : {}), datePublished ? { datePublished: datePublished.split('-').reverse().join('') } : {}));
    });
};
/**
 * Takes the response of a newspaper API and returns a single newspaper
 */
export var reduceToNewspapers = function (withPublicationId) {
    if (withPublicationId === void 0) { withPublicationId = true; }
    return pipe(map(reduceToSingleNewspaper(withPublicationId)));
};
/**
 * Takes the response of a widget API and returns an array of newspapers
 */
export var reduceWidgetGroupsToNewspapers = pipe(prop('widgetGroups'), map(prop('group')), map(prop('newspapers')), flatten, reduceToNewspapers());
/**
 * Takes the response of a widget API and returns an array of news groups
 */
export var reduceWidgetGroupsToNewsGroups = pipe(prop('widgetGroups'), map(prop('group')), flatten, map(function (_a) {
    var name = _a.name, title = _a.title, newspapers = _a.newspapers;
    return ({ id: name, title: title, newspaperIds: newspapers.map(function (newspaper) { return newspaper.id.toString(); }) });
}));
export var dateIndexToNewspapers = function (dateIndex) { return flatten(Object.values(dateIndex)); };
export var safeImgUrl = function (url) { return process.env.API_BASE_URL + "/" + url; };
export var dateToUrlDate = function (date) { return date === null || date === void 0 ? void 0 : date.format('DDMMYYYY'); };
export var dateToApiDate = function (date) { return date === null || date === void 0 ? void 0 : date.format('YYYYMMDD'); };
/**
 * Takes a date string of format DDMMYYYY, and converts it into a string MMYYYY
 */
export var toMonthString = function (date) { return date.slice(2); };
/**
 * Returns the timestamp of the provided day at noon.
 * If nothing is provided returns the timestamp of the current date.
 */
export var getDateTimestamp = function (date) { return (stringToMoment(date).hour(12).minute(0).second(0).millisecond(0).valueOf() // eslint-disable-line
); };
/**
 * Returns a new array (of provided length) of future timestamps starting from the provided one
 * @param length
 * @param startingTimestamp
 * @param unit
 */
export var getNextDaysTimestamps = function (length, startingTimestamp, unit) {
    if (length === void 0) { length = 15; }
    if (startingTimestamp === void 0) { startingTimestamp = getDateTimestamp(); }
    if (unit === void 0) { unit = 'days'; }
    return map(function (index) { return moment(startingTimestamp).add(index, unit).valueOf(); }, range(1, length + 1));
};
/**
 * Returns a new array (of provided length) of past timestamps starting from the provided one
 * @param length
 * @param startingTimestamp
 * @param unit
 */
export var getPastDaysTimestamps = function (length, startingTimestamp, unit) {
    if (length === void 0) { length = 15; }
    if (startingTimestamp === void 0) { startingTimestamp = getDateTimestamp(); }
    if (unit === void 0) { unit = 'days'; }
    return map(function (index) { return moment(startingTimestamp).subtract(index, unit).valueOf(); }, reverse(range(1, length + 1)));
};
export var getDateTabsTimestampsFrom = function (timestamp, unit) {
    if (timestamp === void 0) { timestamp = getDateTimestamp(); }
    if (unit === void 0) { unit = 'days'; }
    return __spreadArray(__spreadArray(__spreadArray([], getPastDaysTimestamps(15, timestamp, unit)), [
        timestamp
    ]), getNextDaysTimestamps(15, timestamp, unit));
};
export var throttle = throttleBy(500);
/**
 * Reverts a date stroing of type DDMMYYYY to YYYYMMDD
 *
 * @param value
 */
export var reverseDateString = function (value) { return value.slice(4) + value.slice(2, 4) + value.slice(0, 2); };
/**
 * Converts a date string to a moment
 * Takes into account url date strings (that are reversed from what Moment accepts)
 *
 * @param value
 */
export var stringToMoment = function (value) { return (moment(value).isValid()
    ? moment(value)
    : moment(reverseDateString(value))); };
